import { flowNodePlaysVideo, type FlowNodeRow } from "@/types/flow"

/**
 * Слепок кадра. 128×96 с запечённым размытием: растягивание на ноду не показывает пиксели,
 * а фильтр не пересчитывается при панорамировании.
 */
export const MINIMAP_SWATCH_WIDTH = 128
export const MINIMAP_SWATCH_HEIGHT = 96

const SWATCH_BLUR_PX = 18

const IMAGE_PREFIX = "image\n"
const VIDEO_PREFIX = "video\n"
const IMAGE_ATTEMPTS = 8
const IMAGE_BATCH = 8
const VIDEO_FRAME_TIMEOUT_MS = 8000

export type FlowMinimapMediaSource = Pick<FlowNodeRow, "kind" | "content_type" | "media_url" | "media_thumb_url">

const masters = new Map<string, HTMLCanvasElement>()
const listeners = new Map<string, Set<() => void>>()
const inflight = new Set<string>()

let mounts = 0
let session = 0

const paintQueue: Array<() => void> = []
let paintEpoch = 0
let paintScheduled = false

type VideoJob = { nodeId: string; key: string; url: string; token: number }
const videoQueue: VideoJob[] = []
let videoPumping = false
let activeVideo: HTMLVideoElement | null = null
let finishActiveGrab: ((frame: HTMLVideoElement | null) => void) | null = null

/** Та же развилка, что у карточки: видео берёт media_url, картинка — thumb. */
export function flowMinimapMediaKey(node: FlowMinimapMediaSource): string | null {
  const video = flowNodePlaysVideo(node)
  const preview = node.media_thumb_url || node.media_url
  if (!preview) return null
  if (!video) return `${IMAGE_PREFIX}${preview}`
  const url = node.media_url || preview
  return `${VIDEO_PREFIX}${url}`
}

export function getMinimapSwatch(key: string): HTMLCanvasElement | null {
  return masters.get(key) ?? null
}

export function subscribeMinimapSwatch(key: string, onChange: () => void): () => void {
  let set = listeners.get(key)
  if (!set) {
    set = new Set()
    listeners.set(key, set)
  }
  set.add(onChange)
  return () => {
    set.delete(onChange)
    if (set.size === 0) listeners.delete(key)
  }
}

export function retainMinimapSwatches(): () => void {
  mounts += 1
  return () => {
    mounts -= 1
    if (mounts > 0) return
    mounts = 0
    cancelMinimapSwatchWork()
  }
}

export function ensureMinimapSwatch(nodeId: string, key: string): void {
  if (masters.has(key) || inflight.has(key)) return
  const parsed = parseKey(key)
  if (!parsed) return
  inflight.add(key)
  if (parsed.kind === "image") {
    sampleImage(nodeId, key, parsed.url, session, IMAGE_ATTEMPTS)
    return
  }
  videoQueue.push({ nodeId, key, url: parsed.url, token: session })
  if (!videoPumping) void pumpVideo()
}

export function cloneMinimapSwatch(master: HTMLCanvasElement): HTMLCanvasElement {
  const canvas = document.createElement("canvas")
  canvas.width = MINIMAP_SWATCH_WIDTH
  canvas.height = MINIMAP_SWATCH_HEIGHT
  canvas.getContext("2d")?.drawImage(master, 0, 0)
  return canvas
}

function parseKey(key: string): { kind: "image" | "video"; url: string } | null {
  if (key.startsWith(IMAGE_PREFIX)) {
    const url = key.slice(IMAGE_PREFIX.length)
    return url ? { kind: "image", url } : null
  }
  if (key.startsWith(VIDEO_PREFIX)) {
    const url = key.slice(VIDEO_PREFIX.length)
    return url ? { kind: "video", url } : null
  }
  return null
}

function publish(key: string, canvas: HTMLCanvasElement) {
  masters.set(key, canvas)
  const subs = listeners.get(key)
  if (!subs) return
  for (const listener of subs) listener()
}

function cancelMinimapSwatchWork() {
  session += 1
  paintEpoch += 1
  paintQueue.length = 0
  paintScheduled = false
  videoQueue.length = 0
  const finish = finishActiveGrab
  finish?.(null)
  releaseVideo(activeVideo)
  for (const key of [...inflight]) {
    if (!masters.has(key)) inflight.delete(key)
  }
}

function sampleImage(nodeId: string, key: string, url: string, token: number, attemptsLeft: number) {
  if (token !== session) return
  if (masters.has(key)) {
    inflight.delete(key)
    return
  }
  const img = cardImage(nodeId, url)
  if (!img) {
    if (attemptsLeft <= 0) {
      inflight.delete(key)
      return
    }
    schedulePaint(() => sampleImage(nodeId, key, url, token, attemptsLeft - 1))
    return
  }
  if (img.complete) {
    if (img.naturalWidth > 0) schedulePaint(() => paintSource(key, token, img))
    else inflight.delete(key)
    return
  }
  const onLoad = () => {
    cleanup()
    if (token !== session) return
    schedulePaint(() => paintSource(key, token, img))
  }
  const onError = () => {
    cleanup()
    if (token !== session) return
    inflight.delete(key)
  }
  const cleanup = () => {
    img.removeEventListener("load", onLoad)
    img.removeEventListener("error", onError)
  }
  img.addEventListener("load", onLoad)
  img.addEventListener("error", onError)
}

function paintSource(key: string, token: number, source: CanvasImageSource) {
  if (token !== session) return
  if (masters.has(key)) {
    inflight.delete(key)
    return
  }
  try {
    const canvas = drawSwatch(source)
    if (canvas) publish(key, canvas)
  } catch {
    // Битый или пустой кадр: нода остаётся серой.
  }
  inflight.delete(key)
}

function schedulePaint(work: () => void) {
  const epoch = paintEpoch
  paintQueue.push(work)
  if (paintScheduled) return
  paintScheduled = true
  requestAnimationFrame(() => runPaintQueue(epoch))
}

function runPaintQueue(epoch: number) {
  if (epoch !== paintEpoch) return
  for (let i = 0; i < IMAGE_BATCH && paintQueue.length > 0; i += 1) {
    paintQueue.shift()?.()
  }
  if (paintQueue.length > 0) {
    requestAnimationFrame(() => runPaintQueue(epoch))
    return
  }
  paintScheduled = false
}

async function pumpVideo() {
  if (videoPumping) return
  videoPumping = true
  try {
    while (videoQueue.length > 0) {
      const job = videoQueue.shift()
      if (!job || job.token !== session) continue
      if (masters.has(job.key)) {
        inflight.delete(job.key)
        continue
      }
      const live = readyCardVideo(job.nodeId, job.url)
      if (live) {
        schedulePaint(() => paintSource(job.key, job.token, live))
        continue
      }
      const frame = await grabVideoFrame(job.url)
      if (job.token !== session) {
        releaseVideo(frame)
        break
      }
      if (frame && frame.videoWidth > 0) paintSource(job.key, job.token, frame)
      else inflight.delete(job.key)
      releaseVideo(frame)
    }
  } finally {
    videoPumping = false
    if (videoQueue.length > 0) void pumpVideo()
  }
}

function grabVideoFrame(url: string): Promise<HTMLVideoElement | null> {
  return new Promise((resolve) => {
    const video = document.createElement("video")
    const holder = document.createElement("div")
    holder.setAttribute("aria-hidden", "true")
    holder.style.position = "fixed"
    holder.style.width = "0"
    holder.style.height = "0"
    holder.style.overflow = "hidden"
    holder.style.pointerEvents = "none"
    holder.appendChild(video)
    document.body.appendChild(holder)

    activeVideo = video
    video.muted = true
    video.playsInline = true
    video.preload = "auto"

    let settled = false
    const finish = (frame: HTMLVideoElement | null) => {
      if (settled) return
      settled = true
      if (finishActiveGrab === finish) finishActiveGrab = null
      window.clearTimeout(timer)
      if (!frame) releaseVideo(video)
      resolve(frame)
    }
    finishActiveGrab = finish
    const timer = window.setTimeout(() => finish(null), VIDEO_FRAME_TIMEOUT_MS)
    const ready = () => {
      if (video.videoWidth > 0 && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) finish(video)
    }
    video.addEventListener("loadeddata", ready)
    video.addEventListener("canplay", ready)
    video.addEventListener("error", () => finish(null))
    video.src = url
    video.load()
  })
}

function releaseVideo(video: HTMLVideoElement | null) {
  if (!video) return
  video.removeAttribute("src")
  video.load()
  video.parentElement?.remove()
  if (activeVideo === video) activeVideo = null
}

function drawSwatch(source: CanvasImageSource): HTMLCanvasElement | null {
  const canvas = document.createElement("canvas")
  canvas.width = MINIMAP_SWATCH_WIDTH
  canvas.height = MINIMAP_SWATCH_HEIGHT
  const ctx = canvas.getContext("2d")
  if (!ctx) return null
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = "high"
  // Картинку рисуем больше холста: край размытия не должен уходить в прозрачность.
  const pad = SWATCH_BLUR_PX * 2
  ctx.filter = `blur(${SWATCH_BLUR_PX}px)`
  ctx.drawImage(source, -pad, -pad, MINIMAP_SWATCH_WIDTH + pad * 2, MINIMAP_SWATCH_HEIGHT + pad * 2)
  ctx.filter = "none"
  return canvas
}

function cardImage(nodeId: string, url: string): HTMLImageElement | null {
  const img = nodeRoot(nodeId)?.querySelector("img")
  if (!(img instanceof HTMLImageElement) || !mediaUrlMatches(img.src, url)) return null
  return img
}

function readyCardVideo(nodeId: string, url: string): HTMLVideoElement | null {
  const video = nodeRoot(nodeId)?.querySelector("video")
  if (!(video instanceof HTMLVideoElement) || !mediaUrlMatches(video.src, url)) return null
  if (video.readyState < HTMLMediaElement.HAVE_CURRENT_DATA || video.videoWidth <= 0) return null
  return video
}

function nodeRoot(nodeId: string): Element | null {
  if (!/^\d+$/.test(nodeId)) return null
  return document.querySelector(`.react-flow__node[data-id="${nodeId}"]`)
}

function mediaUrlMatches(actual: string, expected: string): boolean {
  if (!actual || !expected) return false
  if (actual === expected) return true
  try {
    return new URL(actual, window.location.href).href === new URL(expected, window.location.href).href
  } catch {
    return false
  }
}
